import React, { createContext, useContext, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { modals } from './modalsMap';

import { BaseModal } from '@/widgets/Modals/ui';
import { useModal } from '@/features/Modals/hooks';
import { showModalAction } from '@/shared/store/modals/actions';
import { ModalName } from '@/shared/store/modals/constants';
import { useHideModal } from '@/components/Modals/ModalWrapperHooks';

interface ModalContextProps {
    showModal: (name: ModalName, title?: string, props?: Record<string, any>) => void;
    hideModal: () => void;
}

const ModalContext = createContext<ModalContextProps | undefined>(undefined);

export const ModalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { modalName, modalTitle, modalProps, onClose } = useModal();

    const dispatch = useDispatch();

    const {
        modalComponent,
        closable,
        defaultModalTitle = '',
        className = '',
    } = modals.get(modalName) || {};
    const title = modalTitle || defaultModalTitle;
    const description = modalProps?.description || '';
    const icon = modalProps?.icon;
    const note = modalProps?.note;
    const border = modalProps?.border;
    const isClosable = modalProps?.closable ?? closable ?? true;

    const { handleCloseModal } = useHideModal(onClose);

    const location = useLocation();

    useEffect(() => {
        if (modalName) {
            handleCloseModal();
        }
    }, [location.pathname]);

    const hideModal = useCallback(() => {
        handleCloseModal();
    }, [handleCloseModal]);

    const showModal = useCallback(
        (name: ModalName, title?: string, props?: Record<string, any>) => {
            dispatch(showModalAction({ modalName: name, modalTitle: title, modalProps: props }));
        },
        [],
    );

    return (
        <ModalContext.Provider value={{ showModal, hideModal }}>
            {children}
            <BaseModal
                open={!!modalName}
                onClose={handleCloseModal}
                title={title}
                description={description}
                className={className}
                isClosable={isClosable}
                note={note}
                border={border}
                icon={icon}
            >
                {modalComponent}
            </BaseModal>
        </ModalContext.Provider>
    );
};

export const useModalContext = (): ModalContextProps => {
    const context = useContext(ModalContext);
    if (!context) {
        throw new Error('useModalContext must be used within a ModalProvider');
    }
    return context;
};
